import { css, StyleSheet } from 'aphrodite';
import { type FC, useEffect } from 'react';
import { Button } from '../../../web/components-v2/Button';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { formatCallerId } from './formatCallerId';
import { IconPhone } from './icons';

const REPEAT_MS = 9_000;
const TONE_HZ = 440;
const BEEP_S = 0.18;
const GAP_S = 0.12;
const VOLUME = 0.08;

function playDoubleBeep(context: AudioContext): void {
	const start = context.currentTime;
	for (const offset of [0, BEEP_S + GAP_S]) {
		const oscillator = context.createOscillator();
		const gain = context.createGain();
		oscillator.frequency.value = TONE_HZ;
		gain.gain.value = VOLUME;
		oscillator.connect(gain).connect(context.destination);
		oscillator.start(start + offset);
		oscillator.stop(start + offset + BEEP_S);
	}
}

function useCallWaitingTone(): void {
	useEffect(() => {
		let context: AudioContext;
		try {
			context = new AudioContext();
		} catch (err) {
			console.warn('Call-waiting tone unavailable', err);
			return;
		}
		const beep = () => playDoubleBeep(context);
		beep();
		const timer = setInterval(beep, REPEAT_MS);
		return () => {
			clearInterval(timer);
			void context.close();
		};
	}, []);
}

export type CallWaitingBannerProps = {
	from: string;
	callerDisplay?: string;
	onDismiss: () => void;
};

/** Shown during a live call when a callback comes in, with a short repeating tone in place of the ring. */
export const CallWaitingBanner: FC<CallWaitingBannerProps> = ({ from, callerDisplay, onDismiss }) => {
	useCallWaitingTone();
	const number = formatCallerId(from);

	return (
		<div className={css(styles.banner)} role='status'>
			<IconPhone />
			<span className={css(styles.text)}>
				Callback from <strong>{callerDisplay ?? number}</strong>
				{callerDisplay ? <span className={css(styles.number)}> · {number}</span> : null}
				<span className={css(styles.number)}> · End this call to answer</span>
			</span>
			<Button variant='outline' size='sm' onClick={onDismiss}>
				Dismiss
			</Button>
		</div>
	);
};

const styles = StyleSheet.create({
	banner: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACE[12],
		padding: `${SPACE[8]}px ${SPACE[20]}px`,
		borderBottom: '1px solid var(--border)',
		background: 'var(--accent-soft)',
		color: 'var(--ink)',
		fontSize: THEME.typography.fontSizes.sm,
		flexShrink: 0,
	},
	text: {
		flex: 1,
		minWidth: 0,
		...THEME.common.truncate,
	},
	number: {
		color: 'var(--ink-muted)',
	},
});
